import type React from 'react';
import { useState, useCallback } from 'react';

import { Button, TextField, useTheme } from '@mui/material';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import NodeWrapper from '../NodeWrapper';

import type { NodeMenuConfig } from '../../dataProvider/types';
import type { NodeProps } from '@xyflow/react';

interface NodeSelectorNodeProps extends NodeProps {
  nodeMenuConfig: NodeMenuConfig;
}

/**
 * Renders the NodeSelectorNode component.
 *
 * @return {React.ReactElement} The JSX element representing the NodeSelectorNode component.
 */
export default function NodeSelectorNode({ nodeMenuConfig, id }: NodeSelectorNodeProps): React.ReactElement {
  const reactFlowInstance = useReactFlow();
  const translate = useTranslate();
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const handleMenuItemClick = useCallback(
    (nodeType: string) => {
      reactFlowInstance.setNodes((nodes) => nodes.map((node) => (node.id === id ? { ...node, type: nodeType } : node)));
    },
    [reactFlowInstance, id]
  );

  const lowerSearchTerm = searchTerm.toLowerCase();
  const filteredItems = nodeMenuConfig.flatMap((category) =>
    category.items.filter((item) => translate(item.label).toLowerCase().includes(lowerSearchTerm))
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && filteredItems.length === 1 && filteredItems[0]?.name !== undefined) {
      handleMenuItemClick(filteredItems[0].name);
    }
  };

  return (
    <NodeWrapper condensed>
      <TextField
        placeholder={translate('ra.action.search')}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        autoFocus
        sx={{
          backgroundColor: 'background.paper',
          '& .MuiInputBase-root': {
            borderRadius: `calc(${theme.shape.borderRadius} - 2px)`,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      />
      <div>
        {filteredItems.map((item) => (
          <div key={item.label}>
            <Button
              type='button'
              variant='contained'
              sx={{ borderRadius: 0, margin: '0 0 0 0', width: '100%' }}
              onClick={() => {
                handleMenuItemClick(item.name);
              }}
            >
              {translate(item.label)}
            </Button>
          </div>
        ))}
      </div>
      <Handle type='target' position={Position.Left} id='node-selector-target' />
    </NodeWrapper>
  );
}
